import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

/* =====================================================
   THUNKS
===================================================== */

/* ----------------- CREATE BOOKING ----------------- */
export const createBooking = createAsyncThunk(
    "booking/create",
    async ({ servicemenId, payload }, { rejectWithValue }) => {
        try {
            const { data } = await api.post(
                `/bookings/create-booking/${servicemenId}`,
                payload
            );
            return data.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Create booking failed"
            );
        }
    }
);

/* ----------------- CUSTOMER BOOKINGS ----------------- */
export const fetchCustomerBookings = createAsyncThunk(
    "booking/fetchCustomer",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get(
                "/bookings/get-customer-booking/history"
            );
            return data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Fetch failed");
        }
    }
);

/* ----------------- SERVICEMEN BOOKINGS ----------------- */
export const fetchServiceMenBookings = createAsyncThunk(
    "booking/fetchServiceMen",
    async (servicemenId, { rejectWithValue }) => {
        try {
            const { data } = await api.get(
                `/bookings/servicemen-bookings/${servicemenId}`
            );
            return data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Fetch failed");
        }
    }
);

/* ----------------- GET ALL BOOKINGS (ADMIN) ----------------- */
export const fetchAllBookings = createAsyncThunk(
    "booking/fetchAll",
    async (
        {
            servicemenId,
            status,
            bookingId,
            page = 1,
            limit = 50,
        } = {},
        { rejectWithValue }
    ) => {
        try {
            const params = {};

            if (servicemenId) params.servicemenId = servicemenId;
            if (status) params.status = status;
            if (bookingId) params.bookingId = bookingId;
            params.page = page;
            params.limit = limit;

            const { data } = await api.get("/bookings/all-bookings", {
                params,
            });

            return data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || "Fetch all bookings failed"
            );
        }
    }
);

/* ----------------- SERVICEMEN APPROVE / REJECT ----------------- */
export const servicemenUpdateBooking = createAsyncThunk(
    "booking/servicemenAction",
    async ({ bookingId, action }, { rejectWithValue }) => {
        try {
            const { data } = await api.put(
                `/bookings/booking-update/action/${bookingId}`,
                { action }
            );
            return data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Action failed");
        }
    }
);

/* ----------------- UPDATE BOOKING STATUS ----------------- */
export const updateBookingStatus = createAsyncThunk(
    "booking/updateStatus",
    async ({ bookingId, status }, { rejectWithValue }) => {
        try {
            const { data } = await api.put(
                `/bookings/update/booking-status/${bookingId}`,
                { status }
            );
            return data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Update failed");
        }
    }
);

/* ----------------- CUSTOMER CANCEL BOOKING ----------------- */
export const cancelBookingByCustomer = createAsyncThunk(
    "booking/cancel",
    async (bookingId, { rejectWithValue }) => {
        try {
            const { data } = await api.put(
                `/bookings/cancel-booking/${bookingId}`
            );
            return data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Cancel failed");
        }
    }
);

/* =====================================================
   SLICE
===================================================== */

const bookingSlice = createSlice({
    name: "booking",
    initialState: {
        bookings: [],
        meta: null,          // ✅ for pagination
        loading: false,
        error: null,
        success: false,
    },

    reducers: {
        clearBookingState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
        },
    },

    extraReducers: (builder) => {
        builder

            /* CREATE */
            .addCase(createBooking.pending, (state) => {
                state.loading = true;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bookings.unshift(action.payload);
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* CUSTOMER BOOKINGS */
            .addCase(fetchCustomerBookings.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCustomerBookings.fulfilled, (state, action) => {
                state.loading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchCustomerBookings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* SERVICEMEN BOOKINGS */
            .addCase(fetchServiceMenBookings.fulfilled, (state, action) => {
                state.loading = false;
                state.bookings = action.payload;
            })

            /* ADMIN – ALL BOOKINGS */
            .addCase(fetchAllBookings.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllBookings.fulfilled, (state, action) => {
                state.loading = false;
                state.bookings = action.payload.data;
                state.meta = action.payload.meta;
            })
            .addCase(fetchAllBookings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* UPDATE / APPROVE / REJECT */
            .addCase(servicemenUpdateBooking.fulfilled, (state, action) => {
                const index = state.bookings.findIndex(
                    (b) => b._id === action.payload._id
                );
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
            })

            .addCase(updateBookingStatus.fulfilled, (state, action) => {
                const index = state.bookings.findIndex(
                    (b) => b._id === action.payload._id
                );
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
            })

            /* CANCEL */
            .addCase(cancelBookingByCustomer.fulfilled, (state, action) => {
                const index = state.bookings.findIndex(
                    (b) => b._id === action.payload._id
                );
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
            });
            
    },
});

export const { clearBookingState } = bookingSlice.actions;
export default bookingSlice.reducer;
